import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Input,
  Alert,
  Spinner,
} from "reactstrap";
import { invoiceDetails, listInvoices, updateInvoiceStatus } from "actions/ledger";
import { format } from "date-fns";
import NotificationAlert from "react-notification-alert";
import ReactBsTable from "components/Tables/ReactBsTable";
import Breadcrumbs from "components/BreadCrumb/Breadcrumbs";
import { showAlert } from "components/Common/commonFunctions";
import { FaExclamationTriangle } from "react-icons/fa";
import { downloadInvoice } from "actions/ledger";

// =============== MODAL STYLES ===============
const modalStyles = {
  header: {
    borderBottom: "2px solid #f0f0f0",
    paddingBottom: "18px",
    paddingTop: "18px",
  },
  title: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#2c3e50",
    letterSpacing: "0.3px",
  },
  body: {
    padding: "24px",
    maxHeight: "calc(100vh - 200px)",
    overflowY: "auto",
  },
};

const InvoiceDetails = () => {
  const location = useLocation();
  const notificationAlertRef = useRef(null);
  
  // Get the row data from navigation state
  const rowData = location.state;
  const [detail, setDetail] = useState(null);
  const [voidInvoices, setVoidInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  // Reject modal state
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // ✅ View Invoice Modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return "$0.00";
    }
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
  };

  // Format month display
  const formatMonthDisplay = (monthString) => {
    if (!monthString) return "-";
    try {
      const [year, month] = monthString.split("-");
      if (!year || !month) return monthString;
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return format(date, "MMMM yyyy");
    } catch (e) {
      return monthString;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "MMM dd, yyyy hh:mm a");
    } catch (e) {
      return dateString;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      PENDING: { color: "warning", label: "Pending" },
      ACCEPTED: { color: "success", label: "Accepted" },
      REJECTED: { color: "danger", label: "Rejected" },
      OPEN: { color: "warning", label: "Open" },
      PAID: { color: "success", label: "Paid" },
      VOID: { color: "danger", label: "Void" },
      LOCK: { color: "primary", label: "Locked" },
    };
    const s = statusMap[status] || statusMap.PENDING;
    return <Badge color={s.color}>{s.label}</Badge>;
  };

  const getLedgerStatusBadge = (status) => {
    const statusMap = {
      OPEN: { color: "warning", label: "Open" },
      POSTED: { color: "info", label: "Posted" },
      LOCK: { color: "primary", label: "Locked" },
      PAID: { color: "success", label: "Paid" },
      RERUN: { color: "danger", label: "Rerun" },
    };
    const s = statusMap[status] || statusMap.OPEN;
    return <Badge color={s.color}>{s.label}</Badge>;
  };

  // Notify function
  const notify = (type, message) => {
    if (notificationAlertRef.current) {
      notificationAlertRef.current.notificationAlert({
        place: "tr",
        message: <div>{message}</div>,
        type,
        icon: "tim-icons icon-bell-55",
        autoDismiss: 5,
      });
    }
  };

  // Load invoice details from API using ID from rowData
  const loadDetail = async () => {
    const invoiceId = rowData?.ID || rowData?.id;
    
    if (!invoiceId) {
      notify("warning", "No invoice ID provided");
      return;
    }

    setIsLoading(true);
    try {
      const res = await invoiceDetails(invoiceId);
      if (res?.code === 200 && res?.data) {
        const data = res.data;
        setDetail({
          ID: data.ID || data.id,
          publisherID: data.publisherID || data.publisher_id || data.publisherId,
          ledgerID: data.ledgerID || data.ledger_id || data.ledgerId,
          companyName: data.companyName || data.publisherName || data.publisher,
          publisherStatus: data.publisherStatus || data.PublisherStatus,
          invoiceNumber: data.invoiceNumber || data.InvoiceNumber,
          invoicePath: data.invoicePath || data.InvoicePath,
          uploadedBy: data.uploadedBy || data.UploadedBy,
          uploadedByName: data.uploadedByName || "-",
          uploadedOn: data.uploadedOn||"-",
          rejectedReason: data.rejectedReason || data.RejectedReason,
          status: data.status || data.Status,
          month: data.month || data.Month,
          monthDisplay: formatMonthDisplay(data.month || data.Month) || data.month,
          revenue: data.revenue || data.Revenue || 0,
          netPayable: data.netPayable || data.NetPayable || 0,
          adjustmentTotal: data.adjustmentTotal || data.AdjustmentTotal || 0,
          advanceApplied: data.advanceApplied || data.AdvanceApplied || 0,
          openingBalance: data.openingBalance || data.OpeningBalance || 0,
          closingBalance: data.closingBalance || data.ClosingBalance || 0,
          ledgerStatus: data.ledgerStatus || data.LedgerStatus || "",
          createdBy: data.createdBy || "",
          createdAt: data.createdAt || "",
          updatedAt: data.updatedAt || "",
        });
      } else {
        setDetail(null);
        notify("warning", "Invoice not found");
      }
    } catch (err) {
      console.error("Failed to load invoice detail", err);
      setDetail(null);
      notify("danger", "Failed to load invoice details");
    } finally {
      setIsLoading(false);
    }
  };

  // Load void invoices
  const loadVoidInvoices = async () => {
    setIsListLoading(true);
    try {
      const filters = [];
      filters.push({
        field: "Status",
        condition: "in",
        filterValues: ["PENDING", "VOID", "REJECTED"],
      }, {
        field: "PublisherID",
        condition: "eq",
        filterValues: [JSON.stringify(rowData?.publisherID)],
      });
      const res = await listInvoices(1, null, null, filters);
      if (res?.code === 200 && res?.data) {
        const data = res.data;
        const items = data.LedgerInvoice || data.data || data.invoices || [];
        setVoidInvoices(Array.isArray(items) ? items : []);
      } else {
        setVoidInvoices([]);
      }
    } catch (err) {
      console.error("Failed to load void invoices", err);
      setVoidInvoices([]);
    } finally {
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    if (!rowData) {
      notify("warning", "No invoice data provided");
    } else {
      loadDetail();
    }
    loadVoidInvoices();
    // eslint-disable-next-line
  }, [rowData]);

  // Field Component (matching bank details style)
  const Field = ({ label, value }) => (
    <div className="mb-3">
      <Label className="text-muted small mb-1" style={{ fontSize: "12px", fontWeight: "600" }}>
        {label}
      </Label>
      <div className="font-weight" style={{ fontSize: "14px", color: "#2c3e50" }}>
        {value || "-"}
      </div>
    </div>
  );

  // Void Invoices Columns
  const voidInvoiceColumns = [
    {
      dataField: "invoiceNumber",
      text: "Invoice Number",
      formatter: (cell, row) => (
        <div>
          <strong>{row.invoiceNumber || "-"}</strong>
        </div>
      ),
      sort: true,
    },
    {
      dataField: "month",
      text: "Month",
      formatter: (cell) => cell || "-",
      sort: true,
    },
    {
      dataField: "revenue",
      text: "Revenue",
      formatter: (cell) => formatCurrency(cell),
      headerAlign: "right",
      align: "right",
      sort: true,
    },
    {
      dataField: "adjustmentTotal",
      text: "Adjustments",
      formatter: (cell) => formatCurrency(cell || 0),
      headerAlign: "right",
      align: "right",
      sort: true,
    },
    {
      dataField: "netPayable",
      text: "Net Payable",
      formatter: (cell) => <span className="fw-bold">{formatCurrency(cell)}</span>,
      headerAlign: "right",
      align: "right",
      sort: true,
    },
    {
      dataField: "status",
      text: "Invoice Status",
      formatter: (cell) => getStatusBadge(cell),
      sort: true,
    },
    {
      dataField: "uploadedByName",
      text: "Uploaded By",
      formatter: (cell) => cell || "-",
      sort: true,
    },
        {
      dataField: "uploadedOn",
      text: "Uploaded On",
      formatter: (cell) => cell || "-",
      sort: true,
    },
  ];

  // Handle download
  const downloadFile = (blobData, fileName, contentType) => {
    const blob = new Blob([blobData], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");

    link.href = url;
    link.download = fileName;

    window.document.body.appendChild(link);
    link.click();

    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async (row) => {
    console.log(row);
    try {
      const res = await downloadInvoice(row.ID || row.id);

      // Check if response is an error (JSON object with code/message)
      if (res?.code && (res?.code !== 200)) {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
        return;
      }

      // Determine filename and content type
      let fileName = row.invoiceNumber || "invoice";
      let contentType = "application/octet-stream";

      // Try to get file extension from the invoice path/url
      const invoicePath = row.invoicePath || row.InvoicePath || "";
      if (invoicePath) {
        const pathParts = invoicePath.split("/");
        const fullFileName = pathParts[pathParts.length - 1];

        if (fullFileName) {
          const extension = fullFileName.split(".").pop().toLowerCase();
          fileName = `${fileName}.${extension}`;

          switch (extension) {
            case "pdf":
              contentType = "application/pdf";
              break;
            case "png":
              contentType = "image/png";
              break;
            case "jpg":
            case "jpeg":
              contentType = "image/jpeg";
              break;
            case "gif":
              contentType = "image/gif";
              break;
            case "doc":
              contentType = "application/msword";
              break;
            case "docx":
              contentType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
              break;
            default:
              contentType = "application/octet-stream";
          }
        }
      }

      if (res instanceof Blob || res instanceof ArrayBuffer || typeof res === "string") {
        downloadFile(res, fileName, contentType);
        notify("success", `Downloading ${fileName}`);
      } else {
        notify("danger", "Invalid file response");
      }
    } catch (error) {
      console.error("Download error:", error);
      notify("danger", "Failed to download invoice");
    }
  };

  // ✅ Handler to open View Invoice Modal
  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsViewModalOpen(true);
  };

  // =============== ACCEPT INVOICE HANDLER ===============
  const handleAcceptInvoice = async () => {
    const invoiceId = detail?.ID || detail?.id;
    if (!invoiceId) {
      notify("danger", "Invoice ID not found");
      return;
    }

    showAlert({
      title: "Accept Invoice",
      message: `Are you sure you want to accept invoice "${detail.invoiceNumber}" for ${detail.companyName}?`,
      type: "success",
      confirmStyle: "secondary",
      cancelStyle: "btn btn-primary btn-gradient-primary",
      confirmText: "Yes, Accept",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setIsActionLoading(true);
          const payload = {
            status: "ACCEPTED"
          };
          const response = await updateInvoiceStatus(invoiceId, payload);
          if (response?.code === 200) {
            notify("success", `Invoice "${detail.invoiceNumber}" accepted successfully!`);
            setDetail(prev => ({
              ...prev,
              status: "ACCEPTED"
            }));
            await loadDetail();
            await loadVoidInvoices();
          } else {
            notify("danger", response?.message || "Failed to accept invoice");
          }
        } catch (error) {
          console.error("Error accepting invoice:", error);
          notify("danger", "Something went wrong while accepting invoice");
        } finally {
          setIsActionLoading(false);
        }
      },
      onCancel: () => {},
    });
  };

  // =============== REJECT INVOICE HANDLER ===============
  const handleRejectInvoice = () => {
    setRejectReason("");
    setIsRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectReason || rejectReason.trim() === "") {
      notify("danger", "Please provide a reason for rejection");
      return;
    }

    const invoiceId = detail?.ID || detail?.id;
    if (!invoiceId) {
      notify("danger", "Invoice ID not found");
      return;
    }

    try {
      setIsActionLoading(true);
      const payload = {
        status: "REJECTED",
        rejectedReason: rejectReason.trim()
      };
      const response = await updateInvoiceStatus(invoiceId, payload);
      if (response?.code === 200) {
        notify("success", `Invoice "${detail.invoiceNumber}" rejected successfully!`);
        setIsRejectModalOpen(false);
        setRejectReason("");
        setDetail(prev => ({
          ...prev,
          status: "REJECTED",
          rejectedReason: rejectReason.trim()
        }));
        await loadDetail();
        await loadVoidInvoices();
      } else {
        notify("danger", response?.message || "Failed to reject invoice");
      }
    } catch (error) {
      console.error("Error rejecting invoice:", error);
      notify("danger", "Something went wrong while rejecting invoice");
    } finally {
      setIsActionLoading(false);
    }
  };

  // If no data, show message
  if (!rowData) {
    return (
      <>
        <div className="rna-container">
          <NotificationAlert ref={notificationAlertRef} />
        </div>
        <div className="action-form-container">
          <Container fluid className="mt--6">
            <Card className="advertiser-card">
              <Breadcrumbs />
              <CardBody>
                <div className="text-center my-4">
                  <i className="fa fa-file-invoice fa-3x text-muted mb-3" />
                  <div className="text-muted">No invoice data available</div>
                  <Link to="/admin/invoices" className="btn btn-primary mt-3">
                    <i className="fa fa-arrow-left mr-1" /> Back to Invoices
                  </Link>
                </div>
              </CardBody>
            </Card>
          </Container>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <div className="action-form-container">
        <Container fluid className="mt--6">
          <Card className="advertiser-card">
            <Breadcrumbs />
            <CardBody>
              {isLoading && !detail ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                  <i className="fa fa-spinner fa-spin" style={{ fontSize: "30px", color: "#4f46e5" }} />
                </div>
              ) : detail ? (
                <>
                  {/* Header Section */}
                  <div 
                    className="d-flex justify-content-between align-items-start mb-4 rounded"
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    <div>
                      <h5 className="mb-1" style={{ fontSize: "24px", fontWeight: "bold", color: "#333" }}>
                        {detail.invoiceNumber || detail.InvoiceNumber || "-"}
                      </h5>
                      <p className="text-muted mb-0">{detail.companyName || detail.publisherName || "-"}</p>
                    </div>
                    <div className="text-end">
                      <div>{getStatusBadge(detail.status || detail.Status)}</div>
                      <div className="mt-1">
                        <small className="text-muted">{detail.monthDisplay || formatMonthDisplay(detail.month) || "-"}</small>
                      </div>
                    </div>
                  </div>

                  <Row>
                    {/* Left Column - Publisher & Ledger Info */}
                    <Col md={6}>
                      <Field label="Publisher Name" value={detail.companyName || detail.publisherName} />
                      <Field label="Invoice Number" value={detail.invoiceNumber || detail.InvoiceNumber} />
                      <Field label="Invoice Status" value={getStatusBadge(detail.status || detail.Status)} />
                      <Field label="Uploaded By" value={detail.uploadedByName || "-"} />
                          <Field 
                        label="Net Payable" 
                        value={<span className="fw-bold text-primary">{formatCurrency(detail.netPayable || detail.NetPayable)}</span>} 
                      />
                       <Field label="Uploaded On" value={detail.uploadedOn } />
                      {/* <Field label="Created At" value={formatDate(detail.createdAt || detail.CreatedAt)} /> */}
                      {(detail.updatedAt || detail.UpdatedAt) && (
                        <Field label="Updated At" value={formatDate(detail.updatedAt || detail.UpdatedAt)} />
                      )}
                    </Col>

                    {/* Right Column - Ledger & Financial Info */}
                    <Col md={6}>
                      <Field label="Ledger Month" value={detail.monthDisplay || formatMonthDisplay(detail.month)} />
                      <Field label="Revenue" value={formatCurrency(detail.revenue || detail.Revenue)} />
                      <Field label="Adjustments" value={formatCurrency(detail.adjustmentTotal || detail.AdjustmentTotal || 0)} />
                      <Field label="Advance Applied" value={formatCurrency(detail.advanceApplied || detail.AdvanceApplied || 0)} />
                  
                      <Field 
                        label="Total Payble" 
                        value={<span className="fw-bold text-success">{formatCurrency(detail.netPayable - detail.advanceApplied || 0)}</span>} 
                      />
                    </Col>
                  </Row>

                  {/* Rejection Reason */}
                  {(detail.rejectedReason || detail.RejectedReason) && (
                    <div className="mt-4 p-3">
                      <Label className="text-danger small mb-1" style={{ fontSize: "12px", fontWeight: "600" }}>
                        <i className="fa fa-exclamation-triangle mr-1" />
                        Rejected Reason
                      </Label>
                      <div className="text-danger">{detail.rejectedReason || detail.RejectedReason}</div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-4 d-flex flex-wrap gap-2">
                    {(detail.invoicePath || detail.InvoicePath) && (
                      <>
                        <Button
                          color="primary"
                          className="btn-gradient-primary"
                          onClick={() => handleViewInvoice(detail)}
                        >
                          <i className="fa fa-eye mr-1" /> View Invoice File
                        </Button>
                        <Button
                          color="info"
                          onClick={() => handleDownload(detail)}
                        >
                          <i className="fa fa-download mr-1" /> Download
                        </Button>
                      </>
                    )}

                    {/* Accept / Reject buttons only when status is PENDING */}
                    {detail.status === "PENDING" && (
                      <>
                        <Button
                          color="success"
                          onClick={handleAcceptInvoice}
                          disabled={isActionLoading}
                        >
                          {isActionLoading ? (
                            <><Spinner size="sm" className="me-1" /> Processing...</>
                          ) : (
                            <><i className="fa fa-check mr-1" /> Accept Invoice</>
                          )}
                        </Button>
                        <Button
                          color="secondary"
                          onClick={handleRejectInvoice}
                          disabled={isActionLoading}
                        >
                          <i className="fa fa-times mr-1" /> Reject Invoice
                        </Button>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center my-4">
                  <i className="fa fa-file-invoice fa-3x text-muted mb-3" />
                  <div className="text-muted">Invoice not found</div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Void Invoices History Table */}
          <Card className="mt-4">
            <ReactBsTable
              title="Invoice History"
              description="Invoice History"
              data={voidInvoices}
              columns={voidInvoiceColumns}
              keyField="ID"
              page={1}
              sizePerPage={10}
              totalRecords={voidInvoices.length}
              showLoader={isListLoading}
              bordered
              hover
              enablePageSizeManagement={false}
              showAddbtn={false}
              showColumnsBtn={false}
              exportCSVBtnShow={false}
            />
          </Card>
        </Container>
      </div>

      {/* ========== VIEW INVOICE MODAL (Same as InvoiceList) ========== */}
      <Modal isOpen={isViewModalOpen} toggle={() => setIsViewModalOpen(false)} centered size="xl">
        <ModalHeader toggle={() => setIsViewModalOpen(false)} style={modalStyles.header}>
          <h5 style={modalStyles.title}>
            Tax Invoice
          </h5>
        </ModalHeader>
        <ModalBody style={modalStyles.body}>
          {selectedInvoice && (
            <div className="invoice-preview p-4">
              {selectedInvoice?.invoicePath && (
                <div style={{ height: '70vh', marginTop: 16 }}>
                  <iframe
                    src={selectedInvoice.invoicePath}
                    title="Invoice Preview"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsViewModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* ========== REJECT INVOICE MODAL ========== */}
      <Modal
        isOpen={isRejectModalOpen}
        toggle={() => {
          setIsRejectModalOpen(false);
          setRejectReason("");
        }}
        centered
        size="lg"
      >
        <ModalHeader
          toggle={() => {
            setIsRejectModalOpen(false);
            setRejectReason("");
          }}
        >
          <h5>Reject Invoice</h5>
        </ModalHeader>
        <ModalBody>
          {detail && (
            <>
              <Alert className="filter-btn-gradient-primary">
                                                                      <FaExclamationTriangle className="me-2" />
                                                 <strong>You are about to reject invoice:</strong>
                                                 <ul className="mb-0 mt-2">
                                                     <li><strong>Invoice Number:</strong> {detail.invoiceNumber}</li>
                  <li><strong>Publisher:</strong> {detail.companyName}</li>
                  <li><strong>Month:</strong> {detail.monthDisplay}</li>
                                                 </ul>
                                                                   </Alert>
              <FormGroup>
                <Label>Reason for Rejection <span className="text-danger">*</span></Label>
                <Input
                  type="textarea"
                  placeholder="Please provide a reason for rejecting this invoice..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={4}
                />
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn-gradient-primary"
            onClick={() => {
              setIsRejectModalOpen(false);
              setRejectReason("");
            }}
          >
            Cancel
          </Button>
          <Button
            color="secondary"
            onClick={handleConfirmReject}
            disabled={!rejectReason || rejectReason.trim() === "" || isActionLoading}
          >
            {isActionLoading ? (
              <><Spinner size="sm" className="me-1" /> Processing...</>
            ) : (
              "Reject Invoice"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default InvoiceDetails;